import Checkbox from "components/checkbox/Checkbox";
import DatePicker from "components/date-picker/DatePicker";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import RadioButton from "components/radio-button";
import messages from "messages";
import moment, { MomentInput } from "moment-timezone";
import { useCallback, useMemo } from "react";
import { Button, Dropdown } from "react-bootstrap";
import getClasses from "utils/getClasses";
import styles from "./SessionRepeatRuleEditor.module.scss";
import {
  MAX_SESSIONS,
  REPEAT_END_TYPE_OPTIONS,
  REPEAT_EVERY_OPTIONS,
  REPEAT_INTERVAL_OPTIONS,
  REPEAT_ON_DAYS_OPTIONS,
} from "./session-repeat-rule-editor-constants";
import { getDefaultRepeatRule } from "./utils/getDefaultRepeatRule";
import { getRepeatEndByType } from "./utils/getRepeatEndByType";
import numberToOrdinal from "./utils/numberToOrdinal";

// types
type Value = InviteSeriesInput["repeat_rule"];
interface SessionRepeatRuleEditorProps {
  value: Value;
  onChange: (arg: Value) => void;
  timezone: string;
  intervalStart: MomentInput;
  className?: string;
  disabled?: boolean;
}

/**
 * renders a collection of user inputs for repeatable session options
 */
const SessionRepeatRuleEditor = ({
  value,
  onChange,
  timezone,
  intervalStart,
  className,
  disabled,
}: SessionRepeatRuleEditorProps) => {
  const defaultRepeatRule = useMemo(
    () =>
      getDefaultRepeatRule({
        intervalStart,
        timezone,
      }),
    [intervalStart, timezone],
  );

  const selectEndRuleType = useCallback(
    (id: "end_after" | "end_date") => {
      if (!value) {
        return;
      }

      onChange({
        ...value,
        repeat_end: getRepeatEndByType({
          intervalStart,
          type: id,
          timezone,
          repeat_every: value.repeat_every,
          repeat_interval: value.repeat_interval,
        }),
      });
    },
    [intervalStart, timezone, value, onChange],
  );

  const monthlyRepeatOptions = useMemo(() => {
    const intervalStartMoment = moment.tz(intervalStart, timezone);
    const day = intervalStartMoment.date();
    const weekday = intervalStartMoment.format("dddd");
    const whichWeekdayInMonth = Math.ceil(day / 7);
    const nthOrLast = whichWeekdayInMonth > 4 ? -1 : whichWeekdayInMonth;
    return [
      {
        label: `On day ${day}`,
        value: undefined,
      },
      {
        label: `On the ${
          nthOrLast === -1 ? "last" : numberToOrdinal(nthOrLast)
        } ${weekday}`,
        value: {
          nth: nthOrLast,
          day_of_week: weekday.substring(0, 3).toLowerCase(),
        },
      },
    ];
  }, [intervalStart, timezone]);

  return (
    <div
      className={getClasses(styles["session-repeat-rule-editor"], className)}
    >
      <Checkbox
        variant={"rectangle"}
        label={messages.repeat}
        checked={value !== undefined}
        onChange={(e) =>
          onChange(!e.target.checked ? undefined : defaultRepeatRule)
        }
        disabled={disabled}
      />

      {!!value && (
        <>
          <div className={styles["options"]}>
            <div className={styles["row-with-dropdown"]}>
              <div className={styles["label"]}>{messages.every}</div>
              {/* repeat every, per 1 week, per 2 weeks, ... */}
              <DropdownToggle
                aria-label={"repeat every"}
                className={styles["layout__dropdown"]}
                title={value.repeat_every}
                titleClassName={styles["layout__dropdown-title"]}
                caretClassName={styles["layout__dropdown-caret"]}
                disabled={disabled}
              >
                {REPEAT_EVERY_OPTIONS.map((repeat_every) => (
                  <Dropdown.Item
                    key={repeat_every}
                    onClick={() =>
                      onChange({
                        ...value,
                        repeat_every,
                      })
                    }
                  >
                    {repeat_every}
                  </Dropdown.Item>
                ))}
              </DropdownToggle>
              {/* repeat interval, per 1 week, per 1 month, ... */}
              <DropdownToggle
                aria-label={"repeat interval"}
                className={styles["layout__dropdown"]}
                title={messages[value.repeat_interval]}
                titleClassName={styles["layout__dropdown-title"]}
                caretClassName={styles["layout__dropdown-caret"]}
                disabled={disabled}
              >
                {Object.entries(REPEAT_INTERVAL_OPTIONS).map(
                  ([repeat_interval, stringId]) => (
                    <Dropdown.Item
                      key={repeat_interval}
                      onClick={() => {
                        if (repeat_interval === value.repeat_interval) {
                          return;
                        }

                        return onChange({
                          ...value,
                          repeat_interval:
                            repeat_interval as keyof typeof REPEAT_INTERVAL_OPTIONS,
                          repeat_on_days:
                            repeat_interval === "week"
                              ? getDefaultRepeatRule({
                                  intervalStart,
                                  timezone,
                                }).repeat_on_days
                              : [],
                        });
                      }}
                    >
                      {messages[stringId]}
                    </Dropdown.Item>
                  ),
                )}
              </DropdownToggle>
            </div>
            {value.repeat_interval === "week" && (
              <div className={styles["repeat-on-days"]}>
                <div className={styles["label"]}>{messages.repeatOn}</div>
                <div className={styles["days"]}>
                  {Object.entries(REPEAT_ON_DAYS_OPTIONS).map(([key, text]) => (
                    <Button
                      key={key}
                      variant={
                        value.repeat_on_days.includes(key)
                          ? "primary"
                          : "outline-primary"
                      }
                      className={getClasses(
                        styles["day"],
                        value.repeat_on_days.includes(key) &&
                          styles["day--checked"],
                      )}
                      onClick={() => {
                        if (!value.repeat_on_days?.includes(key)) {
                          return onChange({
                            ...value,
                            repeat_on_days: [...value.repeat_on_days, key],
                          });
                        }
                        const index = value.repeat_on_days.indexOf(key);

                        return onChange({
                          ...value,
                          repeat_on_days: [
                            ...value.repeat_on_days.slice(0, index),
                            ...value.repeat_on_days.slice(index + 1),
                          ],
                        });
                      }}
                      disabled={disabled}
                    >
                      {text.substring(0, 1)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {value.repeat_interval === "month" && (
              <div className={styles["repeat-on-days"]}>
                <div className={styles["label"]}>{messages.repeatOn}</div>
                <DropdownToggle
                  aria-label={"repeat interval"}
                  className={styles["layout__dropdown"]}
                  title={
                    monthlyRepeatOptions[
                      value.repeat_on_nth_day_of_week_in_month ? 1 : 0
                    ].label
                  }
                  titleClassName={styles["layout__dropdown-title"]}
                  caretClassName={styles["layout__dropdown-caret"]}
                  disabled={disabled}
                >
                  {monthlyRepeatOptions.map(({ label, value: optionValue }) => (
                    <Dropdown.Item
                      key={label}
                      onClick={() =>
                        onChange({
                          ...value,
                          repeat_on_nth_day_of_week_in_month: optionValue,
                        })
                      }
                    >
                      {label}
                    </Dropdown.Item>
                  ))}
                </DropdownToggle>
              </div>
            )}

            <div className={styles["end-option-panel"]}>
              <div className={styles["label"]}>{messages.ends}</div>

              <div className={styles["end-option-row"]}>
                <RadioButton
                  name="ends-radio-button-group"
                  label="On"
                  checked={
                    value.repeat_end.type ===
                    REPEAT_END_TYPE_OPTIONS.end_date.id
                  }
                  onChange={() => selectEndRuleType("end_date")}
                  className={styles["radio-button"]}
                  disabled={disabled}
                />
                <DatePicker
                  returnStartOfDay
                  wrapperClassName={styles["date-picker"]}
                  timezone={timezone}
                  dateFormat={"MM/dd/yyyy"}
                  selected={
                    value.repeat_end.type ===
                    REPEAT_END_TYPE_OPTIONS.end_date.id
                      ? new Date(value.repeat_end?.end_date)
                      : undefined
                  }
                  onChange={(date: Date) =>
                    onChange({
                      ...value,
                      repeat_end: {
                        type: REPEAT_END_TYPE_OPTIONS.end_date.id,
                        [REPEAT_END_TYPE_OPTIONS.end_date.id]:
                          date.toISOString(),
                      },
                    })
                  }
                  disabled={
                    disabled ||
                    value.repeat_end.type !==
                      REPEAT_END_TYPE_OPTIONS.end_date.id
                  }
                />
              </div>
              <div className={styles["end-option-row"]}>
                <RadioButton
                  name="ends-radio-button-group"
                  label="After"
                  checked={
                    value.repeat_end.type ===
                    REPEAT_END_TYPE_OPTIONS.end_after.id
                  }
                  onChange={() => selectEndRuleType("end_after")}
                  className={styles["radio-button"]}
                  disabled={disabled}
                />
                <DropdownToggle
                  aria-label={"repeat end value"}
                  className={styles["layout__dropdown"]}
                  title={
                    value.repeat_end.type ===
                    REPEAT_END_TYPE_OPTIONS.end_after.id
                      ? value.repeat_end.end_after
                      : 5
                  }
                  titleClassName={styles["layout__dropdown-title"]}
                  caretClassName={styles["layout__dropdown-caret"]}
                  disabled={
                    disabled ||
                    value.repeat_end.type !==
                      REPEAT_END_TYPE_OPTIONS.end_after.id
                  }
                >
                  {Array.from({ length: MAX_SESSIONS }, (_, i) => i + 1).map(
                    (end_after) => (
                      <Dropdown.Item
                        key={end_after}
                        onClick={() => {
                          if (
                            (value.repeat_end as { end_after: number })
                              .end_after === end_after
                          ) {
                            return;
                          }
                          return onChange({
                            ...value,
                            repeat_end: {
                              type: REPEAT_END_TYPE_OPTIONS.end_after.id,
                              [REPEAT_END_TYPE_OPTIONS.end_after.id]: end_after,
                            },
                          });
                        }}
                      >
                        {end_after}
                      </Dropdown.Item>
                    ),
                  )}
                </DropdownToggle>
                <div>{messages.sessions.toLowerCase()}</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SessionRepeatRuleEditor;
